import { useState, useEffect, useCallback, useRef } from 'react';
import { ShieldAlert, ChevronLeft, ChevronRight, Pencil, X, Check, AlertTriangle, RefreshCw, ChevronDown } from 'lucide-react';
import { apiFetch } from '../api/client';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AlarmRow {
  machine_id: string;
  machine_name: string;
  company_name: string;
  area_name: string;
  db_id: number;
  db_name: string;
  axis_id: number;
  point_name: string;
  axis_name: string;
  axis_num: number;
  unit_code: number;
  unit_label: string;
  unit_short: string;
  alarm_category: number;
  mean: number | null;
  std_dev: number | null;
  threshold_moderate: number | null;
  threshold_severe: number | null;
  sample_count: number;
  calculated_at: string | null;
  avg_2week: number | null;
  recent_samples: number | null;
  pct_of_moderate: number | null;
  active_alarm_severity: string | null;
  latest_alarm_at: string | null;
  has_override: boolean;
  moderate_override: number | null;
  severe_override: number | null;
}

interface Summary {
  cat1_count: number;
  cat2_count: number;
  cat3_count: number;
  cat4_count: number;
  total_axes: number;
}

interface FilterOptions {
  databases:  string[];
  companies:  string[];
  areas:      string[];
  unit_codes: { code: number; label: string }[];
}

interface IsoRow {
  unit_code: number;
  unit_label: string;
  unit_short: string;
  moderate: number;
  severe: number;
  standard: string;
  notes: string;
}

interface EditState {
  axis_id: number;
  unit_code: number;
  moderate: string;
  severe: string;
  category: string;
  original_moderate: number | null;
  original_severe: number | null;
  original_category: number;
  auto_moderate: number | null;
  auto_severe: number | null;
  iso_moderate: number | null;
  iso_severe: number | null;
  error: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const CAT_CONFIG: Record<number, { label: string; color: string; bg: string; text: string; border: string }> = {
  1: { label: 'Cat 1', color: 'green',  bg: 'bg-green-100',  text: 'text-green-700',  border: 'border-green-200' },
  2: { label: 'Cat 2', color: 'blue',   bg: 'bg-blue-100',   text: 'text-blue-700',   border: 'border-blue-200'  },
  3: { label: 'Cat 3', color: 'amber',  bg: 'bg-amber-100',  text: 'text-amber-700',  border: 'border-amber-200' },
  4: { label: 'Cat 4', color: 'red',    bg: 'bg-red-100',    text: 'text-red-700',    border: 'border-red-200'   },
};

function pctColor(pct: number | null): string {
  if (pct === null) return 'text-gray-400';
  if (pct > 100) return 'text-red-600 font-semibold';
  if (pct >= 90)  return 'text-orange-500 font-semibold';
  if (pct >= 70)  return 'text-amber-500';
  return 'text-green-600';
}

function fmt(v: number | null | undefined, decimals = 3): string {
  if (v == null) return '—';
  return v.toFixed(decimals);
}

function fmtEastern(ts: string): string {
  if (!ts) return '';
  const [datePart, timePart = '00:00:00'] = ts.split(' ');
  const sep = datePart.includes('/') ? '/' : '-';
  const [y, m, d] = datePart.split(sep).map(Number);
  const [hh, mm] = (timePart || '').split(':').map(Number);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const ampm = hh >= 12 ? 'PM' : 'AM';
  const h12 = hh % 12 || 12;
  return `${months[m-1]} ${d} ${h12}:${String(mm).padStart(2,'0')} ${ampm}`;
}

function CatBadge({ cat }: { cat: number }) {
  const c = CAT_CONFIG[cat] ?? CAT_CONFIG[4];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text} ${c.border} border`}>
      {c.label}
    </span>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function AlarmManager() {
  // Pagination & filters
  const [page, setPage]               = useState(1);
  const [pageSize, setPageSize]       = useState(50);
  const [total, setTotal]             = useState(0);
  const [pages, setPages]             = useState(1);

  const [dbFilter, setDbFilter]           = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [areaFilter, setAreaFilter]       = useState('');
  const [machineSearch, setMachineSearch] = useState('');
  const [unitFilter, setUnitFilter]       = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [activeAlarmsOnly, setActiveAlarmsOnly] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedMachine, setDebouncedMachine] = useState('');

  // Data
  const [rows, setRows]               = useState<AlarmRow[]>([]);
  const [summary, setSummary]         = useState<Summary | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const [isoRows, setIsoRows]         = useState<IsoRow[]>([]);
  const [loading, setLoading]         = useState(true);

  // UI state
  const [editState, setEditState]     = useState<EditState | null>(null);
  const [showIso, setShowIso]         = useState(false);
  const [isoEdit, setIsoEdit]         = useState<Record<number, { moderate: string; severe: string }>>({});
  const [bulkModal, setBulkModal]     = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [toast, setToast]             = useState<string | null>(null);

  // ── Debounce machine search ──────────────────────────────────────────────
  const handleMachineSearch = (val: string) => {
    setMachineSearch(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedMachine(val);
      setPage(1);
    }, 300);
  };

  // ── Show toast ────────────────────────────────────────────────────────────
  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  // ── Fetch filter options + ISO on mount ──────────────────────────────────
  useEffect(() => {
    apiFetch<FilterOptions>('/alarm-manager/filter-options')
      .then(d => setFilterOptions(d))
      .catch(() => {});
    apiFetch<IsoRow[]>('/alarm-manager/iso-thresholds')
      .then(d => setIsoRows(d))
      .catch(() => {});
  }, []);

  // ── Fetch table data ──────────────────────────────────────────────────────
  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({
      page:      String(page),
      page_size: String(pageSize),
    });
    if (dbFilter)           params.set('db_name',      dbFilter);
    if (companyFilter)      params.set('company_name', companyFilter);
    if (areaFilter)         params.set('area_name',    areaFilter);
    if (debouncedMachine)   params.set('machine_name', debouncedMachine);
    if (unitFilter)         params.set('unit_code',    unitFilter);
    if (categoryFilter)     params.set('category',     categoryFilter);
    if (activeAlarmsOnly)   params.set('active_alarms','true');

    apiFetch<{ total: number; page: number; page_size: number; pages: number; rows: AlarmRow[]; summary: Summary }>(
      `/alarm-manager?${params}`
    )
      .then(d => {
        setRows(d.rows);
        setTotal(d.total);
        setPages(d.pages);
        setSummary(d.summary);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, pageSize, dbFilter, companyFilter, areaFilter, debouncedMachine, unitFilter, categoryFilter, activeAlarmsOnly]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset to page 1 on filter change
  useEffect(() => { setPage(1); }, [dbFilter, companyFilter, areaFilter, unitFilter, categoryFilter, activeAlarmsOnly]);

  // ── Edit row ──────────────────────────────────────────────────────────────
  const openEdit = (row: AlarmRow) => {
    const iso = isoRows.find(i => i.unit_code === row.unit_code);
    setEditState({
      axis_id:           row.axis_id,
      unit_code:         row.unit_code,
      moderate:          fmt(row.threshold_moderate),
      severe:            fmt(row.threshold_severe),
      category:          String(row.alarm_category),
      original_moderate: row.threshold_moderate,
      original_severe:   row.threshold_severe,
      original_category: row.alarm_category,
      auto_moderate:     row.has_override ? null : row.threshold_moderate,
      auto_severe:       row.has_override ? null : row.threshold_severe,
      iso_moderate:      iso?.moderate ?? null,
      iso_severe:        iso?.severe   ?? null,
      error: null,
    });
  };

  const saveEdit = async () => {
    if (!editState) return;
    const mod = parseFloat(editState.moderate);
    const sev = parseFloat(editState.severe);
    if (isNaN(mod) || mod <= 0) {
      setEditState({ ...editState, error: 'Moderate threshold must be > 0' });
      return;
    }
    if (isNaN(sev) || sev <= mod) {
      setEditState({ ...editState, error: 'Severe must be greater than moderate' });
      return;
    }
    try {
      await apiFetch(`/alarm-manager/${editState.axis_id}/${editState.unit_code}`, {
        method: 'PATCH',
        body: JSON.stringify({
          moderate_override:       mod,
          severe_override:         sev,
          alarm_category_override: parseInt(editState.category) || null,
        }),
      });
      setEditState(null);
      showToast('Thresholds saved');
      fetchData();
    } catch (e: any) {
      setEditState({ ...editState, error: e?.message || 'Save failed' });
    }
  };

  const resetToAuto = async () => {
    if (!editState) return;
    try {
      await apiFetch(`/alarm-manager/${editState.axis_id}/${editState.unit_code}`, {
        method: 'PATCH',
        body: JSON.stringify({
          moderate_override:       null,
          severe_override:         null,
          alarm_category_override: null,
        }),
      });
      setEditState(null);
      showToast('Reset to automatic thresholds');
      fetchData();
    } catch (e: any) {
      setEditState({ ...editState, error: e?.message || 'Reset failed' });
    }
  };

  // ── Bulk apply ISO ────────────────────────────────────────────────────────
  const handleBulkApply = async () => {
    setBulkLoading(true);
    try {
      const result = await apiFetch<{ updated_count: number }>('/alarm-manager/bulk-apply-iso', {
        method: 'POST',
        body: JSON.stringify({ unit_codes: [0, 2, 6] }),
      });
      setBulkModal(false);
      showToast(`ISO thresholds applied to ${result.updated_count} axes`);
      fetchData();
      // Refresh ISO rows
      apiFetch<IsoRow[]>('/alarm-manager/iso-thresholds').then(d => setIsoRows(d)).catch(() => {});
    } catch (e: any) {
      showToast(e?.message || 'Bulk apply failed');
    } finally {
      setBulkLoading(false);
    }
  };

  // ── ISO inline edit ───────────────────────────────────────────────────────
  const saveIso = async (unit_code: number) => {
    const edit = isoEdit[unit_code];
    if (!edit) return;
    const mod = parseFloat(edit.moderate);
    const sev = parseFloat(edit.severe);
    if (isNaN(mod) || isNaN(sev) || sev <= mod) {
      showToast('Severe must be greater than moderate');
      return;
    }
    try {
      await apiFetch(`/alarm-manager/iso-thresholds/${unit_code}`, {
        method: 'PATCH',
        body: JSON.stringify({ moderate: mod, severe: sev }),
      });
      const updated = await apiFetch<IsoRow[]>('/alarm-manager/iso-thresholds');
      setIsoRows(updated);
      setIsoEdit(prev => { const n = { ...prev }; delete n[unit_code]; return n; });
      showToast('ISO thresholds updated — all Cat 3 axes updated');
    } catch (e: any) {
      showToast(e?.message || 'Update failed');
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
              <ShieldAlert className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-gray-900">Alarm Manager</h2>
              <p className="text-gray-500 text-sm mt-0.5">Sanity check that alarms exist and are effective across all monitored assets</p>
            </div>
          </div>
          <button
            onClick={() => setBulkModal(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-amber-50 border border-amber-200 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors text-sm"
          >
            <ShieldAlert className="w-3.5 h-3.5" />
            Apply ISO to Cat 4
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-4 gap-4">
          {([1, 2, 3, 4] as const).map(cat => {
            const c = CAT_CONFIG[cat];
            const count = cat === 1 ? summary.cat1_count : cat === 2 ? summary.cat2_count : cat === 3 ? summary.cat3_count : summary.cat4_count;
            const desc = cat === 1 ? 'Robust statistical baseline' : cat === 2 ? 'Statistical baseline (extended)' : cat === 3 ? 'Statistical baseline (sparse)' : 'No alarm configured';
            const sub  = cat === 1 ? '≥20 samples, ≥0.25g, mean+3σ (90d)' : cat === 2 ? '≥15 samples, ≥0.25g, mean+3σ (180d)' : cat === 3 ? '≥10 samples, mean+3σ (360d)' : 'Insufficient data';
            return (
              <button
                key={cat}
                onClick={() => { setCategoryFilter(categoryFilter === String(cat) ? '' : String(cat)); setPage(1); }}
                className={`bg-white rounded-xl p-4 border transition-all text-left ${
                  categoryFilter === String(cat)
                    ? `${c.border} border-2 shadow-sm`
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <CatBadge cat={cat} />
                  <span className={`text-2xl font-bold ${c.text}`}>{count.toLocaleString()}</span>
                </div>
                <p className="text-xs font-medium text-gray-700">{desc}</p>
                <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex flex-wrap items-center gap-3">
        <select value={dbFilter} onChange={e => setDbFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-400">
          <option value="">All Databases</option>
          {filterOptions?.databases.map(d => <option key={d} value={d}>{d}</option>)}
        </select>

        <select value={companyFilter} onChange={e => setCompanyFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-400">
          <option value="">All Companies</option>
          {filterOptions?.companies.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <select value={areaFilter} onChange={e => setAreaFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-400">
          <option value="">All Areas</option>
          {filterOptions?.areas.map(a => <option key={a} value={a}>{a}</option>)}
        </select>

        <input
          type="text"
          placeholder="Search machine…"
          value={machineSearch}
          onChange={e => handleMachineSearch(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-400 w-40"
        />

        <select value={unitFilter} onChange={e => setUnitFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-400">
          <option value="">All Parameters</option>
          {filterOptions?.unit_codes.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}
        </select>

        <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}
          className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-400">
          <option value="">All Categories</option>
          <option value="1">Cat 1</option>
          <option value="2">Cat 2</option>
          <option value="3">Cat 3</option>
          <option value="4">Cat 4</option>
        </select>

        <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
          <input type="checkbox" checked={activeAlarmsOnly} onChange={e => setActiveAlarmsOnly(e.target.checked)}
            className="rounded" />
          Active alarms only
        </label>

        {(dbFilter || companyFilter || areaFilter || debouncedMachine || unitFilter || categoryFilter || activeAlarmsOnly) && (
          <button onClick={() => {
            setDbFilter(''); setCompanyFilter(''); setAreaFilter('');
            setMachineSearch(''); setDebouncedMachine('');
            setUnitFilter(''); setCategoryFilter(''); setActiveAlarmsOnly(false);
            setPage(1);
          }} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
            <X className="w-3 h-3" /> Clear filters
          </button>
        )}

        <span className="ml-auto text-xs text-gray-400">{total.toLocaleString()} rows</span>
      </div>

      {/* Main table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                <th className="px-3 py-2.5 text-left w-20">Cat</th>
                <th className="px-3 py-2.5 text-left">Machine</th>
                <th className="px-3 py-2.5 text-left">Point / Axis</th>
                <th className="px-3 py-2.5 text-left">Parameter</th>
                <th className="px-3 py-2.5 text-right">Baseline</th>
                <th className="px-3 py-2.5 text-right">2-Wk Avg</th>
                <th className="px-3 py-2.5 text-right">Moderate</th>
                <th className="px-3 py-2.5 text-right">Severe</th>
                <th className="px-3 py-2.5 text-center">Alarm</th>
                <th className="px-3 py-2.5 text-center">Date</th>
                <th className="px-3 py-2.5 text-center w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 11 }).map((_, j) => (
                      <td key={j} className="px-3 py-2.5">
                        <div className="h-3.5 bg-gray-100 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-3 py-12 text-center text-gray-400 text-sm">
                    No results matching current filters
                  </td>
                </tr>
              ) : rows.map(row => {
                const isEditing = editState?.axis_id === row.axis_id && editState?.unit_code === row.unit_code;
                const rowKey = `${row.axis_id}-${row.unit_code}`;
                return (
                  <>
                    <tr key={rowKey} className={`hover:bg-gray-50 transition-colors ${isEditing ? 'bg-indigo-50' : ''}`}>
                      {/* Category */}
                      <td className="px-3 py-2.5">
                        <CatBadge cat={row.alarm_category} />
                      </td>

                      {/* Machine */}
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-gray-900 text-xs leading-tight">{row.machine_name}</div>
                        <div className="text-xs text-gray-400 leading-tight">{row.company_name} → {row.area_name}</div>
                      </td>

                      {/* Point / Axis */}
                      <td className="px-3 py-2.5">
                        <div className="text-xs font-medium text-gray-800">{row.point_name}</div>
                        <div className="text-xs text-gray-400">{row.axis_name}</div>
                      </td>

                      {/* Parameter */}
                      <td className="px-3 py-2.5">
                        <div className="text-xs text-gray-700">{row.unit_label.split(' ')[0]}</div>
                        <div className="text-xs text-gray-400">{row.unit_short}</div>
                      </td>

                      {/* Baseline (mean) */}
                      <td className="px-3 py-2.5 text-right">
                        {row.mean != null ? (
                          <>
                            <div className="text-xs font-medium text-gray-800">{fmt(row.mean)}</div>
                            <div className="text-xs text-gray-400">{row.sample_count} samples</div>
                          </>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>

                      {/* 2-Week Avg */}
                      <td className="px-3 py-2.5 text-right">
                        {row.avg_2week != null ? (
                          <>
                            <div className={`text-xs font-medium ${pctColor(row.pct_of_moderate)}`}>
                              {fmt(row.avg_2week)}
                            </div>
                            {row.pct_of_moderate != null && (
                              <div className={`text-xs ${pctColor(row.pct_of_moderate)}`}>
                                {row.pct_of_moderate}%
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>

                      {/* Moderate threshold */}
                      <td className="px-3 py-2.5 text-right">
                        {row.threshold_moderate != null ? (
                          <>
                            <div className={`text-xs font-medium ${row.has_override ? 'text-indigo-600' : 'text-gray-800'}`}>
                              {fmt(row.threshold_moderate)}
                            </div>
                            <div className="text-xs text-gray-400">
                              {row.alarm_category === 3 ? 'sparse' : ''}
                              {row.has_override ? 'override' : ''}
                            </div>
                          </>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>

                      {/* Severe threshold */}
                      <td className="px-3 py-2.5 text-right">
                        {row.threshold_severe != null ? (
                          <div className={`text-xs font-medium ${row.has_override ? 'text-indigo-600' : 'text-gray-800'}`}>
                            {fmt(row.threshold_severe)}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>

                      {/* Active alarm */}
                      <td className="px-3 py-2.5 text-center">
                        {row.active_alarm_severity === 'severe' && (
                          <span className="text-xs font-medium text-red-600">🔴 Severe</span>
                        )}
                        {row.active_alarm_severity === 'moderate' && (
                          <span className="text-xs font-medium text-amber-500">🟡 Moderate</span>
                        )}
                      </td>

                      {/* Alarm date */}
                      <td className="px-3 py-2.5 text-center">
                        {row.latest_alarm_at ? (
                          <span className="text-xs text-gray-500 font-mono whitespace-nowrap">
                            {fmtEastern(row.latest_alarm_at)}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-200">—</span>
                        )}
                      </td>

                      {/* Edit */}
                      <td className="px-3 py-2.5 text-center">
                        <button
                          onClick={() => isEditing ? setEditState(null) : openEdit(row)}
                          className="text-gray-400 hover:text-indigo-600 transition-colors"
                          title={isEditing ? 'Cancel edit' : 'Edit thresholds'}
                        >
                          {isEditing ? <X className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
                        </button>
                      </td>
                    </tr>

                    {/* Inline edit row */}
                    {isEditing && editState && (
                      <tr key={`${rowKey}-edit`} className="bg-indigo-50 border-b border-indigo-100">
                        <td colSpan={11} className="px-4 py-3">
                          <div className="max-w-xl">
                            <p className="text-xs font-semibold text-indigo-700 mb-2">
                              Editing: {row.machine_name} — {row.point_name} {row.axis_name} — {row.unit_label.split(' ')[0]}
                            </p>
                            <div className="grid grid-cols-3 gap-3 mb-2">
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Moderate ({row.unit_short})</label>
                                <input
                                  type="number" step="0.001" value={editState.moderate}
                                  onChange={e => setEditState({ ...editState, moderate: e.target.value, error: null })}
                                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-indigo-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Severe ({row.unit_short})</label>
                                <input
                                  type="number" step="0.001" value={editState.severe}
                                  onChange={e => setEditState({ ...editState, severe: e.target.value, error: null })}
                                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-indigo-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Category override</label>
                                <select
                                  value={editState.category}
                                  onChange={e => setEditState({ ...editState, category: e.target.value })}
                                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-indigo-500"
                                >
                                  <option value="1">Cat 1</option>
                                  <option value="2">Cat 2</option>
                                  <option value="3">Cat 3 (Statistical)</option>
                                  <option value="4">Cat 4 (No alarm)</option>
                                </select>
                              </div>
                            </div>

                            {/* Reference values */}
                            <div className="text-xs text-gray-500 space-y-0.5 mb-2">
                              {row.mean != null && (
                                <span className="mr-4">Baseline: μ={fmt(row.mean)} σ={fmt(row.std_dev, 4)}</span>
                              )}
                              {editState.auto_moderate != null && (
                                <span className="mr-4">Auto: mod={fmt(editState.auto_moderate)} sev={fmt(editState.auto_severe)}</span>
                              )}
                              {editState.iso_moderate != null && (
                                <span>ISO: mod={fmt(editState.iso_moderate)} sev={fmt(editState.iso_severe)}</span>
                              )}
                            </div>

                            {editState.error && (
                              <p className="text-xs text-red-600 mb-2">{editState.error}</p>
                            )}

                            <div className="flex gap-2">
                              <button onClick={resetToAuto}
                                className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 transition-colors text-gray-600">
                                <RefreshCw className="w-3 h-3 inline mr-1" />Reset to auto
                              </button>
                              <button onClick={() => setEditState(null)}
                                className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 transition-colors text-gray-600">
                                Cancel
                              </button>
                              <button onClick={saveEdit}
                                className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors flex items-center gap-1">
                                <Check className="w-3 h-3" />Save changes
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Page size:</span>
            {[25, 50, 100].map(n => (
              <button key={n} onClick={() => { setPageSize(n); setPage(1); }}
                className={`px-2 py-0.5 rounded text-xs ${pageSize === n ? 'bg-indigo-600 text-white' : 'hover:bg-gray-100 text-gray-600'}`}>
                {n}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <span>
              {total === 0 ? '0 rows' : `${((page - 1) * pageSize + 1).toLocaleString()}–${Math.min(page * pageSize, total).toLocaleString()} of ${total.toLocaleString()}`}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs">Page {page} of {pages}</span>
              <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ISO Thresholds panel */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <button
          onClick={() => setShowIso(!showIso)}
          className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition-colors"
        >
          <span className="text-sm font-medium text-gray-700">ISO 10816-3 Default Thresholds</span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showIso ? 'rotate-180' : ''}`} />
        </button>

        {showIso && (
          <div className="px-5 pb-4 border-t border-gray-100">
            <table className="w-full text-sm mt-3">
              <thead>
                <tr className="text-xs text-gray-500 uppercase tracking-wide">
                  <th className="pb-2 text-left">Parameter</th>
                  <th className="pb-2 text-right">Moderate</th>
                  <th className="pb-2 text-right">Severe</th>
                  <th className="pb-2 text-left pl-4">Standard</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {isoRows.map(iso => {
                  const editing = isoEdit[iso.unit_code];
                  return (
                    <tr key={iso.unit_code} className="hover:bg-gray-50">
                      <td className="py-2 text-gray-800 font-medium">{iso.unit_label}</td>
                      <td className="py-2 text-right">
                        {editing ? (
                          <input type="number" step="0.01" value={editing.moderate}
                            onChange={e => setIsoEdit(prev => ({ ...prev, [iso.unit_code]: { ...prev[iso.unit_code], moderate: e.target.value } }))}
                            className="w-20 px-1.5 py-0.5 border border-gray-300 rounded text-xs text-right focus:outline-none focus:border-indigo-500"
                          />
                        ) : (
                          <button onClick={() => setIsoEdit(prev => ({ ...prev, [iso.unit_code]: { moderate: String(iso.moderate), severe: String(iso.severe) } }))}
                            className="text-gray-800 hover:text-indigo-600 transition-colors">
                            {fmt(iso.moderate)} {iso.unit_short}
                          </button>
                        )}
                      </td>
                      <td className="py-2 text-right">
                        {editing ? (
                          <div className="flex items-center justify-end gap-1">
                            <input type="number" step="0.01" value={editing.severe}
                              onChange={e => setIsoEdit(prev => ({ ...prev, [iso.unit_code]: { ...prev[iso.unit_code], severe: e.target.value } }))}
                              className="w-20 px-1.5 py-0.5 border border-gray-300 rounded text-xs text-right focus:outline-none focus:border-indigo-500"
                            />
                            <button onClick={() => saveIso(iso.unit_code)} className="text-indigo-600 hover:text-indigo-800 transition-colors">
                              <Check className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => setIsoEdit(prev => { const n = { ...prev }; delete n[iso.unit_code]; return n; })}
                              className="text-gray-400 hover:text-gray-600 transition-colors">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => setIsoEdit(prev => ({ ...prev, [iso.unit_code]: { moderate: String(iso.moderate), severe: String(iso.severe) } }))}
                            className="text-gray-800 hover:text-indigo-600 transition-colors">
                            {fmt(iso.severe)} {iso.unit_short}
                          </button>
                        )}
                      </td>
                      <td className="py-2 pl-4 text-xs text-gray-400">{iso.standard}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-xs text-gray-400 mt-3">
              ISO values apply to Glatfelter Cat 3 axes and manual overrides. Berry & Arcadium Cat 3 use statistical thresholds from rebuild-baselines. Click any value to edit.
            </p>
          </div>
        )}
      </div>

      {/* Bulk apply ISO modal */}
      {bulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => !bulkLoading && setBulkModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <h3 className="text-sm font-semibold text-gray-900">Apply ISO 10816-3 Thresholds</h3>
              </div>
              {!bulkLoading && (
                <button onClick={() => setBulkModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-gray-700">
                This will apply ISO 10816-3 thresholds to all axes currently in <span className="font-semibold text-red-600">Cat 4</span> (no alarm configured), promoting them to <span className="font-semibold text-amber-600">Cat 3</span>. Note: Cat 3 axes for Berry and Arcadium use statistical thresholds from rebuild — ISO apply is for Glatfelter or manual overrides only.
              </p>
              {isoRows.length > 0 && (
                <div className="bg-gray-50 rounded-lg px-3 py-2.5 text-xs text-gray-600 space-y-1">
                  {isoRows.map(iso => (
                    <div key={iso.unit_code} className="flex justify-between">
                      <span>{iso.unit_label}</span>
                      <span>Mod: <strong>{iso.moderate}</strong> / Sev: <strong>{iso.severe}</strong> {iso.unit_short}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-400">Cat 1, 2, and 3 axes are not affected.</p>
            </div>
            <div className="px-5 pb-4 flex gap-2">
              <button onClick={() => setBulkModal(false)} disabled={bulkLoading}
                className="flex-1 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50">
                Cancel
              </button>
              <button onClick={handleBulkApply} disabled={bulkLoading}
                className="flex-1 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5">
                {bulkLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : null}
                {bulkLoading ? 'Applying…' : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
